import { onboardingService } from '~/api'

import { dataLayerService } from '~/services/data-layer-service'

const ONBOARDING_WIZARD_STATE_KEY = 'onboarding-wizard-state'

const getInitialState = () => {
  const savedState = localStorage.getItem(ONBOARDING_WIZARD_STATE_KEY)
  return savedState
    ? JSON.parse(savedState)
    : {
      activeStep: 0,
      productIds: [],
      categoryIds: [],
      colorSchemeId: 'orange',
      banner: null,
      productsInfo: null,
      categoriesInfo: [],
      themeRequestId: null,
      storeList: localStorage.getItem('storeList') ? JSON.parse(localStorage.getItem('storeList')) : [],
      marketingDealTag: null,
      premiumTheme: null,
      hasBeenPaid: false,
    }
}

const saveState = (state) => {
  localStorage.setItem(ONBOARDING_WIZARD_STATE_KEY, JSON.stringify(state))
}

export const clearOnboardingLocalStorage = () => {
  try {
    localStorage.removeItem(ONBOARDING_WIZARD_STATE_KEY)
  } catch (e) {
    console.error('Failed to clear onboarding state—localStorage:', e)
  }
}

const state = () => ({ ...getInitialState() })

const getters = {}

const mutations = {
  setActiveStep(state, step) {
    state.activeStep = step
  },
  setHasBeenPayed(state, hasBeenPaid) {
    state.hasBeenPaid = hasBeenPaid
  },
  setCategories(state, categories) {
    state.categoryIds = categories
  },
  setProductsIds(state, products) {
    state.productIds = products
  },
  setColorScheme(state, colorSchemeId) {
    state.colorSchemeId = colorSchemeId
  },
  setBanner(state, banner) {
    state.banner = banner
  },
  setProductsInfo(state, productsInfo) {
    state.productsInfo = productsInfo
  },
  setThemeRequestId(state, themeRequestId) {
    state.themeRequestId = themeRequestId

  },
  setCategoriesInfo(state, categoriesInfo) {
    state.categoriesInfo = categoriesInfo
  },
  saveStateToLocalStorage(state) {
    saveState(state)
  },
  setStoreList(state, stores) {
    state.storeList = stores
    saveState(state)
  },
  clearState(state) {
    clearOnboardingLocalStorage()
    Object.assign(state, getInitialState())
  },
  setMarketingDealTag(state, tag) {
    state.marketingDealTag = tag
    saveState(state)
  },
  setPremiumTheme(state, theme) {
    state.premiumTheme = theme
    saveState(state)
  },
}

function camelToSnake(str) {
  return str.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase()
}


const actions = {
  async setActiveStep({ state, commit, rootState }, step) {
    const skipTracking = (state.activeStep === 1 && state.categoryIds?.length === state.categoriesInfo?.length) || state.activeStep === 0
    const isLastStep = step === 6

    if (!skipTracking) {
      await onboardingService.sendTrackProgress(state?.activeStep)
    }

    commit('setActiveStep', step)
    commit('saveStateToLocalStorage')

    const { eventName, params } = getStepEventData(step, state)
    if (eventName) {
      dataLayerService.push({
        event: eventName,
        user_id: rootState.auth.user.id,
        email: rootState.auth.user.email,
        ...params,
      })
    }

    if (isLastStep && !skipTracking) {
      await onboardingService.sendTrackProgress(step)
    }
  },

  async getProductsInfo({ commit, state }) {
    try {
      const categories = state.categoryIds || []

      const response = await onboardingService.getWizardProducts(categories)
      const products = {}
      Object.keys(response.data?.data).forEach(key => {

        products[camelToSnake(key)] = {
          data: response.data?.data[key].map(el => ({
            product: { ...el },
          })),
        }
      })
      commit('setProductsInfo', products)
      commit('saveStateToLocalStorage')
    } catch (error) {
      console.error('Error fetching products:', error)
    }
  },
}


function getStepEventData(step, state) {
  const stepData = {
    2: {
      eventName: 'onboarding_niche_selected',
      params: {
        niches: state.categoryIds.map(
          id => state.categoriesInfo.find(category => category.id === id)?.name,
        ).filter(Boolean),
      },
    },
    3: {
      eventName: 'onboarding_products_selected',
      params: { product_count: state.productIds.length },
    },
    4: {
      eventName: 'onboarding_color_scheme_selected',
      params: { scheme: state.colorSchemeId },
    },
    5: {
      eventName: 'onboarding_banner_selected',
      params: { banner_name: state.banner },
    },
    6: {
      eventName: 'onboarding_store_connected',
      params: {},
    },
  }

  const data = stepData[step]

  if (!data) {
    console.warn(`Unknown step: ${step}`)
    return { eventName: '', params: {} }
  }

  return data
}


export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
